/**
 * @engine/redprint — Core Types
 *
 * Visual scripting framework inspired by Unreal Blueprints / Unity Bolt.
 * Defines the fundamental type system: ports, nodes, edges, graphs.
 */

// ============================================================================
// PORT DATA TYPES
// ============================================================================

/**
 * Built-in data types that ports can carry.
 * Extensible — node packs can register custom types via string literals.
 */
export type PortDataType =
  | 'flow'      // execution flow (no data payload)
  | 'bool'
  | 'i32'
  | 'f32'
  | 'f64'
  | 'numeric'   // generic numeric — resolves to widest connected numeric type
  | 'string'
  | 'vec2'
  | 'vec3'
  | 'vec4'
  | 'quat'      // quaternion {x,y,z,w}
  | 'color'     // packed ABGR u32 or {r,g,b,a}
  | 'entity'    // entity handle (i32)
  | 'any'       // accepts anything (runtime checked)
  | (string & Record<never, never>);  // extensible

/** Compile-time set of numeric types for coercion rules */
export const NUMERIC_TYPES: ReadonlySet<PortDataType> = new Set([
  'i32', 'f32', 'f64', 'numeric',
]);

/** Numeric type promotion order (lowest → highest precision) */
export const NUMERIC_RANK: ReadonlyMap<PortDataType, number> = new Map([
  ['i32', 0],
  ['f32', 1],
  ['f64', 2],
  ['numeric', 1], // unresolved numeric defaults to f32 rank
]);

/** Types that can be implicitly converted TO a target type */
export const IMPLICIT_COERCIONS: ReadonlyMap<PortDataType, ReadonlySet<PortDataType>> = new Map<PortDataType, ReadonlySet<PortDataType>>([
  ['f32', new Set(['i32'])],             // i32 → f32
  ['f64', new Set(['i32', 'f32'])],      // i32,f32 → f64
  ['numeric', new Set(['i32', 'f32', 'f64'])], // any concrete numeric → numeric
  ['string', new Set(['i32', 'f32', 'f64', 'bool'])], // anything → string
  ['bool', new Set(['i32'])],            // i32 → bool
  ['entity', new Set<PortDataType>(['gameobject'])],   // gameobject → entity
  ['gameobject', new Set<PortDataType>(['entity'])],   // entity → gameobject
]);

// ============================================================================
// PORT DEFINITIONS
// ============================================================================

/**
 * Direction of a port on a node.
 */
export type PortDirection = 'input' | 'output';

/**
 * Describes one port on a node definition (static blueprint).
 */
export interface PortDefinition {
  /** Unique ID within the node (e.g. "a", "result", "exec_in") */
  readonly id: string;

  /** Human-readable label */
  readonly name: string;

  /** Data type carried by this port */
  readonly type: PortDataType;

  /** Direction */
  readonly direction: PortDirection;

  /** Default value when nothing is connected (data ports only) */
  readonly defaultValue?: unknown;

  /** If true, port accepts multiple connections (fan-in for flow, reroute) */
  readonly variadic?: boolean;

  /** Tooltip / description */
  readonly description?: string;
}

// ============================================================================
// NODE DEFINITIONS
// ============================================================================

/**
 * Category tag for organizing nodes in the palette.
 */
export type NodeCategory =
  | 'Events'
  | 'Flow'
  | 'Math'
  | 'Logic'
  | 'Variables'
  | 'Transform'
  | 'Sprite'
  | 'Physics'
  | 'Input'
  | 'Audio'
  | 'VFX'
  | 'Debug'
  | 'Custom'
  | (string & Record<never, never>);

/**
 * Describes one type of node (static blueprint, not an instance).
 *
 * Node packs create these and register via `NodeRegistry.register()`.
 */
export interface NodeDefinition {
  /** Globally unique type ID, e.g. "events.update", "math.add" */
  readonly id: string;

  /** Display name in palette, e.g. "Add", "On Update" */
  readonly name: string;

  /** Category for grouping */
  readonly category: NodeCategory;

  /** Port layout */
  readonly inputs: readonly PortDefinition[];
  readonly outputs: readonly PortDefinition[];

  /** If true, this node is a lifecycle event entry point */
  readonly isEvent?: boolean;

  /** If true, this node is "pure" (no side effects, no flow ports) */
  readonly isPure?: boolean;

  /** Visual */
  readonly color?: string;
  readonly icon?: string;
  readonly description?: string;
  readonly keywords?: readonly string[];  // search keywords

  /**
   * Dynamic port generation.
   * Called when user adds/removes ports at runtime (e.g. "Sequence" node).
   * Return new port definitions based on current node state.
   */
  readonly dynamicPorts?: (config: Record<string, unknown>) => {
    inputs: PortDefinition[];
    outputs: PortDefinition[];
  };
}

// ============================================================================
// NODE INSTANCES (in a graph)
// ============================================================================

/**
 * Position in the visual editor canvas.
 */
export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

/**
 * An instantiated node inside a graph.
 */
export interface NodeInstance {
  /** Unique instance ID (UUID) */
  readonly id: string;

  /** References `NodeDefinition.id` */
  readonly type: string;

  /** Canvas position */
  position: Vec2;

  /**
   * Inline values for input ports that have no incoming edge.
   * Keys are port IDs, values are the literal data.
   */
  readonly inputValues: Record<string, unknown>;

  /** User-set comment / label */
  comment?: string;

  /** Dynamic config for nodes with dynamic ports */
  config?: Record<string, unknown>;
}

// ============================================================================
// EDGES (connections between ports)
// ============================================================================

/**
 * A port reference: which node and which port.
 */
export interface PortRef {
  readonly nodeId: string;
  readonly portId: string;
}

/**
 * A directed edge from an output port to an input port.
 */
export interface Edge {
  readonly id: string;
  readonly from: PortRef;   // output port
  readonly to: PortRef;     // input port
}

// ============================================================================
// VARIABLES
// ============================================================================

/**
 * A graph-scoped variable (becomes a class field in compiled output).
 */
export interface GraphVariable {
  readonly id: string;
  readonly name: string;
  readonly type: PortDataType;
  readonly defaultValue: unknown;

  /** Expose in inspector as @serialize field */
  readonly exposed: boolean;

  /** Tooltip */
  readonly description?: string;
}

// ============================================================================
// GRAPH
// ============================================================================

// ============================================================================
// EDITOR ANNOTATIONS
// ============================================================================

/**
 * A sticky note / comment in the graph editor.
 */
export interface StickyNote {
  readonly id: string;
  position: Vec2;
  size: { width: number; height: number };
  text: string;
  color: string;  // CSS color
}

/**
 * A visual group that wraps several nodes.
 */
export interface NodeGroup {
  readonly id: string;
  name: string;
  position: Vec2;
  size: { width: number; height: number };
  color: string;
  nodeIds: string[];
}

// ============================================================================
// SUBGRAPH
// ============================================================================

/**
 * An exposed port on a subgraph (bridges the outer graph edge ↔ inner graph node).
 */
export interface SubgraphPort {
  readonly id: string;
  readonly name: string;
  readonly type: PortDataType;
  readonly direction: PortDirection;
}

/**
 * A subgraph definition that can be instantiated as a node in parent graphs.
 */
export interface SubgraphDefinition {
  readonly id: string;
  readonly name: string;
  readonly graph: GraphData;
  readonly inputPorts: readonly SubgraphPort[];
  readonly outputPorts: readonly SubgraphPort[];
  readonly description?: string;
  readonly color?: string;
}

// ============================================================================
// CUSTOM TYPE REGISTRATION
// ============================================================================

/**
 * Metadata for a custom port data type.
 * Used by editor (color, widget) and compiler (serialization, default).
 */
export interface CustomTypeDefinition {
  /** Type ID string, e.g. 'texture_handle', 'audio_clip' */
  readonly id: string;
  /** Display name */
  readonly name: string;
  /** Editor port color */
  readonly color: string;
  /** Default value for unconnected ports */
  readonly defaultValue: unknown;
  /** Allowed implicit coerce targets */
  readonly coercesTo?: readonly PortDataType[];
  /** How to serialize the value to a string literal */
  toLiteral?(value: unknown): string;
  /** How to deserialize from a string literal */
  fromLiteral?(literal: string): unknown;
}

/**
 * Complete graph data — the serializable "document".
 */
export interface GraphData {
  /** Unique graph ID */
  readonly id: string;

  /** Human-readable name (becomes class name when compiled) */
  readonly name: string;

  /** All node instances */
  readonly nodes: readonly NodeInstance[];

  /** All edges */
  readonly edges: readonly Edge[];

  /** Graph-scoped variables */
  readonly variables: readonly GraphVariable[];

  /** Sticky notes / comments */
  readonly stickies?: readonly StickyNote[];

  /** Visual node groups */
  readonly groups?: readonly NodeGroup[];

  /** Embedded subgraph definitions */
  readonly subgraphs?: readonly SubgraphDefinition[];

  /** If true, this graph IS a subgraph (has input/output bridge nodes) */
  readonly isSubgraph?: boolean;

  /** Optional metadata */
  readonly metadata?: {
    readonly description?: string;
    readonly category?: string;
    readonly executeInEditor?: boolean;
    readonly icon?: string;
    readonly version?: number;
  };
}

// ============================================================================
// COMPILATION
// ============================================================================

/**
 * Single IR instruction — SSA-like intermediate representation.
 */
export interface IRInstruction {
  readonly kind: 'instruction';

  /** Unique ID (used for referencing results) */
  readonly id: string;

  /** Operation (maps to an AS expression or statement) */
  readonly op: string;

  /**
   * Input references — either:
   * - another instruction's ID (data dependency)
   * - a literal prefixed with `#` (e.g. "#3.14", "#true")
   * - a variable prefixed with `$` (e.g. "$speed")
   */
  readonly inputs: readonly string[];

  /** Result type (void for flow-only ops) */
  readonly resultType: PortDataType;

  /** If true, this instruction has side effects (must not be reordered / eliminated) */
  readonly hasSideEffects?: boolean;

  /** Source node ID (for error mapping) */
  readonly sourceNodeId?: string;
}

/**
 * IR If statement — conditional branching.
 */
export interface IRIf {
  readonly kind: 'if';

  /** Condition expression (instruction ID or literal) */
  readonly condition: string;

  /** Statements to execute if true */
  readonly thenBody: readonly IRStatement[];

  /** Statements to execute if false (optional) */
  readonly elseBody?: readonly IRStatement[];

  /** Source node ID */
  readonly sourceNodeId?: string;
}

/**
 * IR For loop — counted iteration.
 */
export interface IRFor {
  readonly kind: 'for';

  /** Loop index variable name */
  readonly indexVar: string;

  /** Start value (instruction ID or literal) */
  readonly start: string;

  /** End value (instruction ID or literal) */
  readonly end: string;

  /** Loop body statements */
  readonly body: readonly IRStatement[];

  /** Source node ID */
  readonly sourceNodeId?: string;
}

/**
 * IR While loop — condition-based iteration.
 */
export interface IRWhile {
  readonly kind: 'while';

  /** Condition expression (must be re-evaluated each iteration) */
  readonly condition: string;

  /** Loop body statements */
  readonly body: readonly IRStatement[];

  /** Source node ID */
  readonly sourceNodeId?: string;
}

/**
 * IR Break statement — exits the current loop.
 */
export interface IRBreak {
  readonly kind: 'break';
  readonly sourceNodeId?: string;
}

/**
 * IR Continue statement — skips to next iteration.
 */
export interface IRContinue {
  readonly kind: 'continue';
  readonly sourceNodeId?: string;
}

/**
 * Union of all IR statement types.
 */
export type IRStatement = IRInstruction | IRIf | IRFor | IRWhile | IRBreak | IRContinue;

/**
 * A block of IR statements that form one execution path.
 * Maps to one lifecycle method body or a branch.
 */
export interface IRBlock {
  /** Block name, e.g. "update", "onAwake", "branch_0_true" */
  readonly name: string;

  /** Ordered statements (instructions + control flow) */
  readonly statements: readonly IRStatement[];

  /** @deprecated Use statements instead */
  readonly instructions?: readonly IRInstruction[];
}

/**
 * Complete compiled IR for one graph.
 */
export interface IRProgram {
  /** Class name */
  readonly name: string;

  /** Fields (from graph variables) */
  readonly fields: readonly {
    readonly name: string;
    readonly type: PortDataType;
    readonly defaultValue: unknown;
    readonly exposed: boolean;
  }[];

  /** Blocks (one per lifecycle event + helpers) */
  readonly blocks: readonly IRBlock[];
}

/**
 * A compilation diagnostic.
 */
export interface CompileDiagnostic {
  readonly severity: 'error' | 'warning' | 'info';
  readonly message: string;
  readonly nodeId?: string;
  readonly portId?: string;
}

/**
 * Result of compiling a graph.
 */
export interface CompileResult {
  readonly success: boolean;

  /** Generated source code (language depends on backend) */
  readonly source: string | null;

  /** Intermediate representation (for debugging) */
  readonly ir: IRProgram | null;

  /** Diagnostics */
  readonly diagnostics: readonly CompileDiagnostic[];
}

// ============================================================================
// EXECUTION (interpreted mode)
// ============================================================================

/**
 * Value bag for port data during interpreted execution.
 */
export type PortValue = number | boolean | string | null | { x: number; y: number } | { x: number; y: number; z: number } | { x: number; y: number; z: number; w: number };

/**
 * Execution state of one graph instance.
 */
export interface ExecutionState {
  /** Current variable values */
  readonly variables: Map<string, PortValue>;

  /** Cached port outputs (nodeId:portId → value) */
  readonly portCache: Map<string, PortValue>;
}

// ============================================================================
// NODE BEHAVIOUR (compile-time code generation)
// ============================================================================

/**
 * Context passed to a node's compile function.
 * Lets the node emit IR instructions.
 */
export interface NodeCompileContext {
  /** Get the IR value reference for an input port (follows edge or uses inline value) */
  resolveInput(portId: string): string;

  /** Emit an IR instruction and return its result reference */
  emit(op: string, inputs: string[], resultType: PortDataType, sideEffects?: boolean): string;

  /** Get a unique temp variable name */
  tempVar(prefix?: string): string;

  /** Get variable reference by name */
  variable(name: string): string;
}

/**
 * Context passed to a node's execute function (interpreted mode).
 */
export interface NodeExecuteContext {
  /** Read an input port's value */
  getInput<T = PortValue>(portId: string): T;

  /** Write an output port's value */
  setOutput(portId: string, value: PortValue): void;

  /** Read a graph variable */
  getVariable<T = PortValue>(name: string): T;

  /** Write a graph variable */
  setVariable(name: string, value: PortValue): void;

  /** Trigger flow on an output flow port (for branching nodes) */
  triggerFlow(portId: string): void;

  /** Read persistent per-node state (survives across frames) */
  getState<T = unknown>(key: string): T | undefined;

  /** Write persistent per-node state (survives across frames) */
  setState(key: string, value: unknown): void;

  /** Which flow input port triggered this execution (undefined for pure nodes) */
  readonly triggeredPort?: string;

  /** Engine bridge for host API calls (transform, input, etc.) */
  readonly bridge: import('./runtime/ExecutionContext').EngineBridge;

  /** The entity this graph instance is attached to */
  readonly entityId: number;

  /** Engine bridge */
  readonly deltaTime: number;
  readonly time: number;
}

/**
 * Full node behaviour definition — what a node pack provides.
 * Extends NodeDefinition with executable logic + compilation.
 */
export interface NodeBehaviour extends NodeDefinition {
  /**
   * Compile this node to IR instructions.
   * @returns The result reference string for the primary output.
   */
  compile?(ctx: NodeCompileContext): string | void;

  /**
   * Interpret this node directly (editor preview / fallback).
   */
  execute?(ctx: NodeExecuteContext): void;
}
